import { useEffect, useState, useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectUserPermissions } from '@src/store/user/userSlice';
import { sidebarMenuItems } from '../sidebarMenuItems';
import { SELECTED_SIDEBAR_ITEM } from '@src/constants/localStorage';

function mapSidebarItems(items, t) {
    return items.map((item) => ({
        key: item.key,
        icon: item.icon,
        label: item.url ? (
            <Link to={item.url}>{t(item.label)}</Link>
        ) : (
            t(item.label)
        ),
        permissionKey: item.permissionKey,
        children: item.children ? mapSidebarItems(item.children, t) : undefined,
    }));
}

function filterSidebarItems(items, permissions) {
    if (!permissions) return [];

    return items
        .map((item) => {
            const children = item.children
                ? filterSidebarItems(item.children, permissions)
                : undefined;

            const hasParentPermission = item.permissionKey
                ? permissions.effective[item.permissionKey]
                : false;

            const hasAccess =
                hasParentPermission || (children?.length ?? 0) > 0;

            if (!hasAccess) return null;

            return { ...item, children };
        })
        .filter(Boolean);
}

function findMatchingItemWithParent(items, path, parentKey = null) {
    let matches: { matchedItem: any; parentKey: string | null }[] = [];

    for (const item of items) {
        if (item.key === 'dashboard' && path !== '/dashboard') continue;

        if (item.url && path.startsWith(item.url)) {
            matches.push({ matchedItem: item, parentKey });
        }

        if (item.children) {
            const childMatch = findMatchingItemWithParent(
                item.children,
                path,
                item.key
            );
            if (childMatch) matches = matches.concat(childMatch);
        }
    }

    return matches.sort(
        (a, b) =>
            (b.matchedItem.url?.length ?? 0) - (a.matchedItem.url?.length ?? 0)
    )[0];
}

export function useSidebar() {
    const location = useLocation();
    const { t } = useTranslation();
    const permissions = useSelector(selectUserPermissions);

    const [selectedItem, setSelectedItem] = useState(() => {
        return localStorage.getItem(SELECTED_SIDEBAR_ITEM) ?? 'dashboard';
    });
    const [openKeys, setOpenKeys] = useState<string[]>();

    const sidebarItems = useMemo(() => {
        const mapped = mapSidebarItems(sidebarMenuItems, t);
        return filterSidebarItems(mapped, permissions);
    }, [permissions, t]);

    useEffect(() => {
        if (!sidebarItems.length) return;

        const result = findMatchingItemWithParent(
            sidebarMenuItems,
            location.pathname
        );

        if (result) {
            setSelectedItem(result.matchedItem.key);
            setOpenKeys(result.parentKey ? [result.parentKey] : []);
        } else {
            setSelectedItem('');
            setOpenKeys([]);
        }
    }, [location.pathname]);

    useEffect(() => {
        if (selectedItem) {
            localStorage.setItem(SELECTED_SIDEBAR_ITEM, selectedItem);
        }
    }, [selectedItem]);

    return {
        sidebarItems,
        selectedItem,
        openKeys,
        setSelectedItem,
    };
}
